import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import DiscrepancyService from '../services/DiscrepancyService';
import { isEmpty } from 'lodash';

const initialState = {
    data: [],
    datas: [],
    summaryDatas: [],
    formError: [],
    pagination: {
        page: 1,
        perPage: 10,
        total: 0,
        lastPage: 1,
    },
    summaryPagination: {
        page: 1,
        perPage: 10,
        total: 0,
        lastPage: 1,
    },
    summary: null,
    error: null,
    status: '',
    loading: false,
    summaryLoading: false,
    submitLoading: false,
    shopDatas: [],
    shopLoading: false,
};

export const fetchDiscrepancyList = createAsyncThunk(
    'discrepancy/list',
    async ({ perPage, page, keyword, sku, from_date, to_date }, { rejectWithValue }) => {
        try {
            const resp = await DiscrepancyService.List({ perPage, page, keyword, sku, from_date, to_date });
            return resp;
        } catch (e) {
            return rejectWithValue(e.response);
        }
    },
);

export const fetchDiscrepancySummary = createAsyncThunk(
    'discrepancy/summary',
    async ({ perPage, page, search, from_date, to_date }, { rejectWithValue }) => {
        try {
            const resp = await DiscrepancyService.Summary({ per_page: perPage, page, search, from_date, to_date });
            return resp;
        } catch (e) {
            return rejectWithValue(e.response);
        }
    },
);

export const resolveDiscrepancy = createAsyncThunk(
    'discrepancy/resolve',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const resp = await DiscrepancyService.Resolve(id, data);
            return resp;
        } catch (e) {
            return rejectWithValue(e.response);
        }
    },
);

export const markDiscrepancyChecked = createAsyncThunk(
    'discrepancy/mark-checked',
    async ({ shop_id, date, order_id }, { rejectWithValue }) => {
        try {
            const resp = await DiscrepancyService.MarkChecked({ shop_id, date, order_id });
            return { ...resp, shop_id, order_id };
        } catch (e) {
            return rejectWithValue(e.response);
        }
    },
);

export const fetchDiscrepancyGroupByShop = createAsyncThunk(
    'discrepancy/group-by-shop',
    async (params, { rejectWithValue }) => {
        try {
            const resp = await DiscrepancyService.DiscrepancyGroupByShop(params);
            return resp;
        } catch (e) {
            return rejectWithValue(e.response);
        }
    },
);

const discrepancySlice = createSlice({
    name: 'discrepancy',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // --- Summary ---
            .addCase(fetchDiscrepancySummary.pending, (state) => {
                state.summaryLoading = true;
            })
            .addCase(fetchDiscrepancySummary.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.summaryDatas = action.payload?.data?.data?.data || [];
                    state.summaryPagination = action.payload?.data?.data?.pagination || initialState.summaryPagination;
                    state.summary = action.payload?.data?.data?.overall_summary || null;
                } else {
                    state.error = action.payload?.data?.message;
                    toast.error(state.error);
                }
                state.summaryLoading = false;
            })
            .addCase(fetchDiscrepancySummary.rejected, (state, action) => {
                state.error = action.payload?.data;
                state.summaryLoading = false;
            })
            // --- List ---
            .addCase(fetchDiscrepancyList.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDiscrepancyList.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.datas = action.payload?.data?.data?.discrepancies || [];
                    state.pagination = action.payload?.data?.data?.pagination || initialState.pagination;
                    const summary = action.payload?.data?.data?.summary;
                    state.summary = Array.isArray(summary) ? summary[0] : summary || null;
                } else {
                    state.error = action.payload?.data?.message;
                    toast.error(state.error);
                }
                state.loading = false;
            })
            .addCase(fetchDiscrepancyList.rejected, (state, action) => {
                state.error = action.payload?.data;
                state.loading = false;
            })
            // --- Resolve ---
            .addCase(resolveDiscrepancy.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(resolveDiscrepancy.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    const index = state.datas.findIndex(
                        (item) => item.id === action.payload.data.data.id,
                    );
                    if (index !== -1) {
                        state.datas[index] = {
                            ...state.datas[index],
                            ...action.payload.data.data,
                        };
                    }

                    // Update shopDatas for DiscrepancyListByShop
                    const updatedItem = action.payload.data.data;
                    state.shopDatas.forEach((shop) => {
                        const dIndex = shop.discrepancies.findIndex(
                            (item) => item.id === updatedItem.id,
                        );
                        if (dIndex !== -1) {
                            const wasOpen = shop.discrepancies[dIndex].status === 'open';
                            const isNowClosed = updatedItem.status !== 'open';

                            shop.discrepancies[dIndex] = {
                                ...shop.discrepancies[dIndex],
                                ...updatedItem,
                            };

                            if (wasOpen && isNowClosed) {
                                shop.open_count = Math.max(0, shop.open_count - 1);
                            }
                        }
                    });

                    toast.success(action.payload?.data.message);
                } else {
                    const data = action.payload?.data;
                    const detail = Object.values(data?.errors || {}).flat()[0];
                    toast.error(
                        detail
                            ? `${data?.message || 'Error'}: ${detail}`
                            : data?.message || 'Update failed',
                    );
                }
                state.submitLoading = false;
            })
            .addCase(resolveDiscrepancy.rejected, (state, action) => {
                state.submitLoading = false;
                const data = action.payload?.data;
                const title = data?.message;
                const detail = Object.values(data?.errors || {}).flat()[0];

                if (title === 'Insufficient Stock' || detail) {
                    toast.error(
                        detail
                            ? `${title || 'Insufficient Stock'}\n${detail}`
                            : title || 'Something went wrong',
                        { autoClose: 6000 },
                    );
                } else if (isEmpty(data?.errors)) {
                    toast.error(title || 'Something went wrong');
                } else {
                    state.formError = data.errors;
                    toast.error(detail || title || 'Something went wrong');
                }
            })
            // --- Mark Checked ---
            .addCase(markDiscrepancyChecked.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(markDiscrepancyChecked.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    const shopId = action.payload.shop_id;
                    const orderId = action.payload.order_id;
                    const shop = state.shopDatas.find((s) => s.shop_id === shopId);
                    if (shop) {
                        shop.discrepancies = shop.discrepancies.map((item) => {
                            if (orderId && String(item.order_id) !== String(orderId)) {
                                return item;
                            }
                            return {
                                ...item,
                                is_checked: true,
                            };
                        });
                        shop.unchecked_count = shop.discrepancies.filter(
                            (item) => !item.is_checked
                        ).length;
                    }
                    toast.success(action.payload?.data?.message);
                } else {
                    toast.error(action.payload?.data?.message || 'Update failed');
                }
                state.submitLoading = false;
            })
            .addCase(markDiscrepancyChecked.rejected, (state, action) => {
                state.submitLoading = false;
                toast.error(action.payload?.data?.message || 'Something went wrong');
            })
            // --- Group By Shop ---
            .addCase(fetchDiscrepancyGroupByShop.pending, (state) => {
                state.shopLoading = true;
            })
            .addCase(fetchDiscrepancyGroupByShop.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.shopDatas = action.payload?.data?.data || [];
                } else {
                    state.error = action.payload?.data?.message;
                    toast.error(state.error);
                }
                state.shopLoading = false;
            })
            .addCase(fetchDiscrepancyGroupByShop.rejected, (state, action) => {
                state.error = action.payload?.data;
                state.shopLoading = false;
            });
    },
});

export default discrepancySlice.reducer;
